import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  BillingDetails,
  PricingConfiguration,
  CostEstimate,
} from '../components/Billing/types';
import { useAppSelector, useAppDispatch } from './useReduxTypeScript';
import {
  fetchBillingConfig,
  saveBillingConfig as saveBillingConfigThunk,
} from '../utils/redux/slices/billingSlice';
import {
  transformFromApiFormat,
  transformToApiFormat,
  getDefaultBillingDetails,
  getDefaultPricingConfig,
} from '../utils/billing/transformers';

// Use a record for ValidationErrors per lint rule
type ValidationErrors = Record<string, string>;

export const useBillingForm = () => {
  const dispatch = useAppDispatch();
  const billingState = useAppSelector(state => state.billing);

  // Local state for UI-specific data
  const [billingDetails, setBillingDetails] = useState<BillingDetails>(
    getDefaultBillingDetails()
  );
  const [pricingConfig, setPricingConfig] = useState<PricingConfiguration>(
    getDefaultPricingConfig()
  );
  const [billingErrors, setBillingErrors] = useState<ValidationErrors>({});
  const [pricingErrors, setPricingErrors] = useState<ValidationErrors>({});
  const [estimatedCallMinutes, setEstimatedCallMinutes] =
    useState<number>(1000);
  const [estimatedForwardingMinutes, setEstimatedForwardingMinutes] =
    useState<number>(200);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load billing config from API on mount.
  // Ref guard prevents double-dispatch under StrictMode: both effect fires share
  // the same render closure (so `billingState.loading` is still `false` in the
  // second fire), which would otherwise cause the thunk to dispatch twice.
  const didDispatchConfigFetchRef = useRef(false);
  useEffect(() => {
    if (
      !billingState.isConfigLoaded &&
      !billingState.loading &&
      !didDispatchConfigFetchRef.current
    ) {
      didDispatchConfigFetchRef.current = true;
      dispatch(fetchBillingConfig());
    }
    if (billingState.isConfigLoaded) {
      didDispatchConfigFetchRef.current = false;
    }
  }, [dispatch, billingState.isConfigLoaded, billingState.loading]);

  // Reset initialized flag when billing state is cleared (on logout)
  useEffect(() => {
    if (!billingState.config && !billingState.isConfigLoaded && isInitialized) {
      setIsInitialized(false);
      setBillingDetails(getDefaultBillingDetails());
      setPricingConfig(getDefaultPricingConfig());
    }
  }, [billingState.config, billingState.isConfigLoaded, isInitialized]);

  // Update local state ONLY on initial load or after successful save
  useEffect(() => {
    if (billingState.config && !isInitialized) {
      const { billingDetails: apiDetails, pricingConfig: apiPricing } =
        transformFromApiFormat(billingState.config);
      setBillingDetails(apiDetails);
      setPricingConfig(apiPricing);
      setIsInitialized(true);
    }
  }, [billingState.config, isInitialized]);

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateGSTNumber = (gstin: string): boolean => {
    const gstRegex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gstin);
  };

  const validateBillingDetails = useCallback(
    (details: BillingDetails): ValidationErrors => {
      const errors: ValidationErrors = {};

      if (!details.companyName.trim()) {
        errors.companyName = 'Company name is required';
      }

      if (!details.email.trim()) {
        errors.email = 'Email is required';
      } else if (!validateEmail(details.email)) {
        errors.email = 'Invalid email format';
      }

      if (details.gstNumber && !validateGSTNumber(details.gstNumber)) {
        errors.gstNumber = 'Invalid GST number format';
      }

      if (!details.address.street1.trim()) {
        errors.street1 = 'Street address is required';
      }

      if (!details.address.city.trim()) {
        errors.city = 'City is required';
      }

      if (!details.address.state.trim()) {
        errors.state = 'State is required';
      }

      if (!details.address.zipCode.trim()) {
        errors.zipCode = 'ZIP/Postal code is required';
      }

      if (!details.address.country.trim()) {
        errors.country = 'Country is required';
      }

      return errors;
    },
    []
  );

  const validatePricingConfig = useCallback(
    (config: PricingConfiguration): ValidationErrors => {
      const errors: ValidationErrors = {};

      if (config.gstEnabled && !config.gstNumber.trim()) {
        errors.gstNumber = 'GST number is required when GST is enabled';
      }

      if (
        config.gstEnabled &&
        config.gstNumber &&
        !validateGSTNumber(config.gstNumber)
      ) {
        errors.gstNumber = 'Invalid GST number format';
      }

      if (
        config.gstEnabled &&
        (config.gstPercentage < 0 || config.gstPercentage > 100)
      ) {
        errors.gstPercentage = 'GST percentage must be between 0 and 100';
      }

      if (config.callRate <= 0) {
        errors.callRate = 'Call rate must be greater than 0';
      }

      if (config.telephonyMode === 'bundled' && config.telephonyRate <= 0) {
        errors.telephonyRate = 'Telephony rate must be greater than 0';
      }

      if (config.callForwardingEnabled && config.forwardingRate <= 0) {
        errors.forwardingRate = 'Forwarding rate must be greater than 0';
      }

      return errors;
    },
    []
  );

  // Update handlers - Update local state only (no Redux dispatch)
  const updateBillingDetails = useCallback(
    (updates: Partial<BillingDetails>) => {
      setBillingDetails(prev => ({ ...prev, ...updates }));
    },
    []
  );

  const updateBillingAddress = useCallback(
    (updates: Partial<BillingDetails['address']>) => {
      setBillingDetails(prev => ({
        ...prev,
        address: { ...prev.address, ...updates },
      }));
    },
    []
  );

  const updatePricingConfig = useCallback(
    (updates: Partial<PricingConfiguration>) => {
      setPricingConfig(prev => ({ ...prev, ...updates }));
    },
    []
  );

  // Submit handlers
  const handleBillingSubmit = useCallback(
    async (
      onSuccess?: () => void,
      onError?: (errors: ValidationErrors) => void
    ) => {
      const errors = validateBillingDetails(billingDetails);
      setBillingErrors(errors);

      if (Object.keys(errors).length === 0) {
        try {
          // Save billing details along with current pricing config
          const apiPayload = transformToApiFormat(
            billingDetails,
            pricingConfig
          );
          const result = await dispatch(
            saveBillingConfigThunk(apiPayload)
          ).unwrap();

          // After successful save, sync local state with the saved data
          if (result.data) {
            const {
              billingDetails: savedDetails,
              pricingConfig: savedPricing,
            } = transformFromApiFormat(result.data);
            setBillingDetails(savedDetails);
            setPricingConfig(savedPricing);
          }

          onSuccess?.();
          return true;
        } catch (error) {
          console.error('Error saving billing details:', error);
          onError?.(errors);
          return false;
        }
      } else {
        onError?.(errors);
        return false;
      }
    },
    [billingDetails, pricingConfig, validateBillingDetails, dispatch]
  );

  const handlePricingSubmit = useCallback(
    async (
      onSuccess?: () => void,
      onError?: (errors: ValidationErrors) => void
    ) => {
      // Validate both billing details and pricing config
      const billingValidationErrors = validateBillingDetails(billingDetails);
      const pricingValidationErrors = validatePricingConfig(pricingConfig);

      const allErrors = {
        ...billingValidationErrors,
        ...pricingValidationErrors,
      };

      setBillingErrors(billingValidationErrors);
      setPricingErrors(pricingValidationErrors);

      if (Object.keys(allErrors).length === 0) {
        try {
          // Transform to API format and save
          const apiPayload = transformToApiFormat(
            billingDetails,
            pricingConfig
          );
          const result = await dispatch(
            saveBillingConfigThunk(apiPayload)
          ).unwrap();

          // After successful save, sync local state with the saved data
          if (result.data) {
            const {
              billingDetails: savedDetails,
              pricingConfig: savedPricing,
            } = transformFromApiFormat(result.data);
            setBillingDetails(savedDetails);
            setPricingConfig(savedPricing);
          }

          onSuccess?.();
          return true;
        } catch (error) {
          console.error('Error saving billing config:', error);
          onError?.(allErrors);
          return false;
        }
      } else {
        onError?.(allErrors);
        return false;
      }
    },
    [
      billingDetails,
      pricingConfig,
      validateBillingDetails,
      validatePricingConfig,
      dispatch,
    ]
  );

  // Cost calculation
  const costEstimate: CostEstimate = useMemo(() => {
    const callRate = Number(pricingConfig.callRate) || 0;
    const telephonyRate = Number(pricingConfig.telephonyRate) || 0;
    const forwardingRate = Number(pricingConfig.forwardingRate) || 0;
    const gstPercentage = Number(pricingConfig.gstPercentage) || 0;

    // For bundled mode: telephony is already included in call rate
    // For separate mode: telephony is billed separately
    const baseCallCharges = estimatedCallMinutes * callRate;
    const telephonyCharges =
      pricingConfig.telephonyMode === 'separate'
        ? estimatedCallMinutes * telephonyRate
        : 0;
    const callForwarding = pricingConfig.callForwardingEnabled
      ? estimatedForwardingMinutes * forwardingRate
      : 0;

    const subtotal = baseCallCharges + telephonyCharges + callForwarding;
    const gst = pricingConfig.gstEnabled ? (subtotal * gstPercentage) / 100 : 0;
    const total = subtotal + gst;

    return {
      estimatedCallMinutes,
      estimatedForwardingMinutes,
      baseCallCharges: parseFloat(baseCallCharges.toFixed(2)),
      telephonyCharges: parseFloat(telephonyCharges.toFixed(2)),
      callForwarding: parseFloat(callForwarding.toFixed(2)),
      subtotal: parseFloat(subtotal.toFixed(2)),
      gst: parseFloat(gst.toFixed(2)),
      total: parseFloat(total.toFixed(2)),
    };
  }, [
    estimatedCallMinutes,
    estimatedForwardingMinutes,
    pricingConfig.callRate,
    pricingConfig.telephonyRate,
    pricingConfig.telephonyMode,
    pricingConfig.forwardingRate,
    pricingConfig.callForwardingEnabled,
    pricingConfig.gstEnabled,
    pricingConfig.gstPercentage,
  ]);

  return {
    billingDetails,
    pricingConfig,
    billingErrors,
    pricingErrors,
    estimatedCallMinutes,
    estimatedForwardingMinutes,
    costEstimate,
    updateBillingDetails,
    updateBillingAddress,
    updatePricingConfig,
    setEstimatedCallMinutes,
    setEstimatedForwardingMinutes,
    handleBillingSubmit,
    handlePricingSubmit,
    setBillingErrors,
    setPricingErrors,
    // Redux state
    loading: billingState.loading,
    saving: billingState.saving,
    error: billingState.error,
    isConfigLoaded: billingState.isConfigLoaded,
  };
};
